/**
 * Help Dialog Component
 * Displays help information in a modal dialog
 */

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import {
    CheckCircle as CheckIcon
} from '@mui/icons-material';

interface HelpDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    content: {
        description?: string;
        examples?: string[];
        tips?: string[];
        scoring?: {
            base?: string;
            preferred?: string;
            bonus?: string;
        };
    };
}

const HelpDialog: React.FC<HelpDialogProps> = ({ open, onClose, title, content }) => {
    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: 3
                }
            }}
        >
            <DialogTitle sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                fontWeight: 600,
                fontSize: '1.25rem'
            }}>
                {title}
            </DialogTitle>
            <DialogContent sx={{ mt: 2, pb: 2 }}>
                {content.description && (
                    <Typography variant="body1" paragraph>
                        {content.description}
                    </Typography>
                )}

                {content.examples && content.examples.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Examples:
                        </Typography>
                        <List dense>
                            {content.examples.map((example, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <CheckIcon sx={{ mr: 1, fontSize: 18, color: 'success.main' }} />
                                    <ListItemText 
                                        primary={example}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {content.tips && content.tips.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                            Tips:
                        </Typography>
                        <List dense>
                            {content.tips.map((tip, index) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <Typography variant="body2" sx={{ pl: 2 }}>
                                        • {tip}
                                    </Typography>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}

                {content.scoring && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                            Scoring:
                        </Typography>
                        {content.scoring.base && (
                            <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                                <strong>Base (60%):</strong> {content.scoring.base}
                            </Typography>
                        )}
                        {content.scoring.preferred && (
                            <Typography variant="body2" paragraph sx={{ mb: 1 }}>
                                <strong>Preferred (+30%):</strong> {content.scoring.preferred}
                            </Typography>
                        )}
                        {content.scoring.bonus && (
                            <Typography variant="body2" sx={{ mb: 0 }}>
                                <strong>Bonus (+10%):</strong> {content.scoring.bonus}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Got it
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default HelpDialog;
